import type { ThemeManifest } from '@vibeshell/shared';
import { stripMarkdown } from './markdown';

const KEY = 'vs:creator:recent-apps';
// The identity that currently "owns" the recents bucket. localStorage is shared
// per-browser, so this is what isolates one signed-in creator's workspace from
// the next on the same device. See setWorkspaceOwner().
const OWNER_KEY = 'vs:creator:workspace-owner';
const MAX = 8;

/**
 * One point on an app's timeline — the result of the initial generation
 * or a refinement. Carries everything needed to restore that version:
 * its deployed URL, theme manifest, and synthesized JSX module.
 */
export interface AppVersion {
  /** Unix ms when this version was produced. */
  at: number;
  /** Human label — "Original" for the first build, else the refinement instruction. */
  label: string;
  kind: 'create' | 'revise';
  appId: string;
  url: string;
  ogImageUrl: string;
  manifest: ThemeManifest;
  /** Synthesized JSX module — enables both restore and refine-from-here. */
  jsx?: string;
}

export interface RecentApp {
  appId: string;
  url: string;
  ogImageUrl: string;
  prompt: string;
  manifest: ThemeManifest;
  createdAt: number;
  /**
   * Last-touched timestamp — bumped whenever the app is opened or saved.
   * Drives the most-recent-first order in the Workspace + sidebar so that
   * reopening an old app surfaces it to the top without losing the original
   * `createdAt`. Optional for backward-compatibility with entries written
   * before this field existed (legacy entries fall back to `createdAt`).
   */
  updatedAt?: number;
  jsx?: string;
  /**
   * Full version timeline, oldest first. The top-level fields above mirror
   * the *currently shown* version, which may be an earlier one if the user
   * has restored it from the history.
   */
  versions: AppVersion[];
}

/** A stored entry, tolerant of legacy records written before `versions` existed. */
type StoredApp = Omit<RecentApp, 'versions'> & { versions?: AppVersion[] };

/** Backfill a single-version timeline for a legacy entry that predates `versions`. */
function withVersions(a: StoredApp): RecentApp {
  if (a.versions && a.versions.length) return { ...a, versions: a.versions };
  return {
    ...a,
    versions: [
      {
        at: a.createdAt,
        label: 'Original',
        kind: 'create',
        appId: a.appId,
        url: a.url,
        ogImageUrl: a.ogImageUrl,
        manifest: a.manifest,
        jsx: a.jsx
      }
    ]
  };
}

/** Score by which the recents list is ordered — newest-touch first. */
function recencyScore(a: RecentApp): number {
  return a.updatedAt ?? a.createdAt;
}

/**
 * Soft-dedup key for the Workspace display layer. Two entries with the
 * same lowercased trimmed manifest name are treated as "the same logical
 * app" — typically the result of regenerating an app from the same
 * prompt, duplicating an app multiple times, or refining without
 * the supersedes link landing. In each of those cases the older row is
 * functionally stale and showing it triples the Workspace shelf for no
 * user benefit.
 *
 * Strict appId dedup runs FIRST (true duplicates by id); this name pass
 * is the second filter that catches "PortfolioX × 3" with three
 * different ids. If the user genuinely wants two apps with the same
 * name, they can rename one in a future "edit name" flow — that's a
 * far rarer case than the duplicate noise this collapses.
 */
function logicalKey(a: RecentApp): string {
  const name = (a.manifest?.name || '').trim().toLowerCase();
  return name || a.appId;
}

export function getRecentApps(): RecentApp[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredApp[];
    // PASS 1 — strict dedupe by `appId`. Older builds may have written
    // duplicate appIds before the upsert logic landed. Collapse to the
    // newest record per id, strip markdown gunk from prompts so legacy
    // records with raw `## 2. **Split Bill**…` headings render cleanly
    // on Workspace cards.
    const byId = new Map<string, RecentApp>();
    for (const stored of parsed) {
      const app = withVersions(stored);
      app.prompt = stripMarkdown(app.prompt);
      const existing = byId.get(app.appId);
      if (!existing || recencyScore(app) > recencyScore(existing)) {
        byId.set(app.appId, app);
      }
    }
    // PASS 2 — soft dedupe by logical key (lowercased trimmed name).
    // Three regenerations of "PortfolioX" with three different appIds
    // collapse to the single most recently touched row. This is the
    // visual integrity guarantee the Workspace needs.
    const byKey = new Map<string, RecentApp>();
    for (const app of byId.values()) {
      const key = logicalKey(app);
      const existing = byKey.get(key);
      if (!existing || recencyScore(app) > recencyScore(existing)) {
        byKey.set(key, app);
      }
    }
    const cleaned = Array.from(byKey.values()).sort((a, b) => recencyScore(b) - recencyScore(a));
    // Retroactive write-back — if dedup or markdown-strip changed
    // anything, persist the cleaned array so the next read starts from
    // a clean baseline and existing duplicates / markdown gunk are
    // gone for good.
    const reserialised = JSON.stringify(cleaned);
    if (cleaned.length !== parsed.length || reserialised !== raw) {
      try {
        localStorage.setItem(KEY, reserialised);
      } catch {
        /* best-effort — leaves the old payload in place if storage is full */
      }
    }
    return cleaned;
  } catch {
    return [];
  }
}

/**
 * Persist an app to the recents list. `supersedes` lets a refinement
 * replace the entry it was built from, so a refine chain collapses into
 * one card carrying the full version history.
 *
 * UPSERT semantics — if an entry with the same `appId` already exists it
 * is removed before the new record is inserted at the head of the list, so
 * the same app never lands on two cards. The new entry always carries an
 * `updatedAt` stamp so the Workspace ordering reflects the most recent
 * activity.
 *
 * localStorage is ~5MB and a generated app's JSX module is large, so a
 * full store can throw QuotaExceededError. Recents are best-effort —
 * this is called from inside the generation-complete state update, so a
 * throw here would break the whole completion. On a quota failure we
 * progressively shed payload (strip the heavy JSX from older entries,
 * then drop entries) and, in the worst case, give up silently.
 */
export function saveRecentApp(app: RecentApp, supersedes?: string): void {
  // Stamp the touch time — preserve any explicit value the caller passed
  // (a restore-from-version flow may want a deliberate timestamp).
  const stamped: RecentApp = { ...app, updatedAt: app.updatedAt ?? Date.now() };
  const list = getRecentApps().filter((a) => a.appId !== stamped.appId && a.appId !== supersedes);
  list.unshift(stamped);
  const full = list.slice(0, MAX);
  const attempts: RecentApp[][] = [
    full,
    full.map((a, i) => (i === 0 ? a : stripHeavy(a))),
    full.slice(0, 3).map((a, i) => (i === 0 ? a : stripHeavy(a))),
    [stamped]
  ];
  for (const attempt of attempts) {
    try {
      localStorage.setItem(KEY, JSON.stringify(attempt));
      return;
    } catch {
      /* localStorage full — fall through and retry with a smaller payload */
    }
  }
}

/**
 * Bump an existing entry to the head of the list with a fresh `updatedAt`,
 * leaving every other field untouched. Used when an app is reopened — the
 * Workspace then surfaces it as the most-recent card without rewriting any
 * of its data. A no-op if the appId isn't in the list.
 */
export function touchRecentApp(appId: string): void {
  try {
    const list = getRecentApps();
    const entry = list.find((a) => a.appId === appId);
    if (!entry) return;
    saveRecentApp({ ...entry, updatedAt: Date.now() });
  } catch {
    /* best-effort — a touch failure simply leaves the order unchanged */
  }
}

/** Drop the heavy synthesized JSX so a stored entry costs far less space. */
function stripHeavy(a: RecentApp): RecentApp {
  return {
    ...a,
    jsx: undefined,
    versions: a.versions.map((v) => ({ ...v, jsx: undefined }))
  };
}

export function findRecentApp(appId: string): RecentApp | undefined {
  return getRecentApps().find((a) => a.appId === appId);
}

/**
 * Brutal one-shot dedup, called by main.tsx on app boot to guarantee the
 * workspace localStorage payload is clean BEFORE any UI mounts. Strips
 * entries missing an appId, collapses duplicates (keeping the most-
 * recently-touched), sorts newest-first, and UNCONDITIONALLY writes the
 * cleaned array back. On corrupt JSON the key is wiped so the app
 * recovers from any prior broken state instead of crashing.
 *
 * Sister to `getRecentApps`'s per-call dedup — that one is conservative
 * (only writes back if the serialized form actually changed). This one
 * is brutal: it always writes, so any prior duplicate payload is
 * replaced even if some pathological case made the conservative check
 * miss it.
 */
export function purgeDuplicates(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Corrupt payload — wipe so the next save starts clean.
      localStorage.removeItem(KEY);
      return;
    }
    if (!Array.isArray(parsed)) {
      localStorage.removeItem(KEY);
      return;
    }
    // PASS 1 — strict dedupe by appId. Earlier bug appended duplicates
    // with the same id; the trailing copy holds the freshest jsx +
    // manifest, so `>=` wins on ties.
    const byId = new Map<string, StoredApp>();
    for (const entry of parsed) {
      if (!entry || typeof entry !== 'object') continue;
      const stored = entry as StoredApp;
      if (!stored.appId || typeof stored.appId !== 'string') continue;
      const existing = byId.get(stored.appId);
      const existingTime = existing
        ? (existing.updatedAt ?? existing.createdAt ?? 0)
        : -1;
      const incomingTime = stored.updatedAt ?? stored.createdAt ?? 0;
      if (incomingTime >= existingTime) {
        byId.set(stored.appId, stored);
      }
    }
    // PASS 2 — soft dedupe by logical key (lowercased trimmed manifest
    // name). Three regenerations of "PortfolioX" with three different
    // appIds collapse to the single most recently touched row. Mirrors
    // the per-read dedupe in getRecentApps so a boot-time purge and a
    // first-read produce the same set.
    const byKey = new Map<string, StoredApp>();
    for (const stored of byId.values()) {
      const rawName = (stored.manifest as { name?: unknown } | undefined)?.name;
      const name = typeof rawName === 'string' ? rawName.trim().toLowerCase() : '';
      const key = name || stored.appId;
      const existing = byKey.get(key);
      const existingTime = existing
        ? (existing.updatedAt ?? existing.createdAt ?? 0)
        : -1;
      const incomingTime = stored.updatedAt ?? stored.createdAt ?? 0;
      if (incomingTime >= existingTime) {
        byKey.set(key, stored);
      }
    }
    const cleaned = Array.from(byKey.values()).sort((a, b) => {
      const ta = a.updatedAt ?? a.createdAt ?? 0;
      const tb = b.updatedAt ?? b.createdAt ?? 0;
      return tb - ta;
    });
    // Unconditional write — brutal by design. Even if the resulting
    // array looks structurally identical to `parsed`, the write
    // replaces any partial-state from a prior aborted save.
    try {
      localStorage.setItem(KEY, JSON.stringify(cleaned));
    } catch {
      /* quota exhausted — leave as-is; getRecentApps still dedups on read */
    }
  } catch {
    /* defensive — never throw out of a boot-time helper */
  }
}

/**
 * Bind the recents bucket to the signed-in identity — the isolation guarantee.
 *
 * The Workspace localStorage is shared per-browser, so without this a second
 * account signing in on the same device would see the FIRST account's apps (the
 * "new user sees everyone's apps" leak). We wipe the recents ONLY when handing
 * off from one real account to a DIFFERENT identity:
 *   - accountA → accountB  → wipe (different person)
 *   - accountA → signed-out → wipe (A's private workspace must not linger)
 * and we deliberately KEEP the local apps when:
 *   - first run (no owner tag yet) → claim the existing bucket for this identity
 *   - anon → account            → the SAME person signing in for the first time;
 *                                  wiping here would lose a just-built app
 * After a wipe the account-scoped cloud (listCreatorApps, RLS + account_id
 * filter) re-hydrates the correct workspace. Fires `pomela:workspace-reset` so a
 * mounted Workspace re-reads immediately instead of showing a stale flash.
 */
export function setWorkspaceOwner(ownerId: string | null | undefined): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const next = (ownerId || 'anon').trim() || 'anon';
    const prev = localStorage.getItem(OWNER_KEY);
    if (prev === next) return;
    const handoffFromRealAccount = !!prev && prev !== 'anon' && prev !== next;
    if (handoffFromRealAccount) {
      localStorage.removeItem(KEY);
      try {
        window.dispatchEvent(new Event('pomela:workspace-reset'));
      } catch {
        /* no window (SSR/tests) — the removeItem above is the real guarantee */
      }
    }
    localStorage.setItem(OWNER_KEY, next);
  } catch {
    /* best-effort — an identity reconcile must never throw out of boot */
  }
}

/** Remove an app from the recents list — the My Apps "remove" action. */
export function removeRecentApp(appId: string): void {
  try {
    const next = getRecentApps().filter((a) => a.appId !== appId);
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* best-effort — a storage failure simply leaves the entry in place */
  }
}
